import { addDoc, collection, getDocs, limit, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { firestore } from '@src/firebase';
import type { DifficultyId } from '@src/core/Difficulty';

export const SCORES_COLLECTION = 'scores';
export const LEADERBOARD_SIZE = 8;

export type RunOutcome = 'death' | 'victory';

export interface LeaderboardEntry {
  name: string;
  score: number;
  floor: number;
  room: number;
  kills: number;
  combo: number;
  tier: DifficultyId;
  outcome: RunOutcome;
}

export interface RunScoreInput {
  score: number;
  floor: number;
  room: number;
  kills: number;
  combo: number;
  tier: DifficultyId;
  outcome: RunOutcome;
}

/** Firestore accepts 2–24; typed handles are capped tighter. */
export const CONTESTANT_NAME_MAX = 18;
const NAME_STORE_RE = /^[A-Z0-9][A-Z0-9 \-]{1,23}$/;
const NAME_INPUT_RE = /^[A-Z0-9][A-Z0-9 \-]{1,17}$/;
const NAME_KEY = 'wa.contestant';
const WRITE_AT_KEY = 'wa.scoreAt';
const WRITE_COOLDOWN_MS = 4000;
const MAX_SCORE = 99_999_999;
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;
const NAME_ILLEGAL = /[^A-Z0-9 \-]/g;

const ADJ = ['ASH', 'BONE', 'CINDER', 'GOLD', 'GRIM', 'HEXED', 'IRON', 'MOON', 'RUNE', 'VEIL', 'VOID', 'WILD'] as const;
const NOUN = ['CROW', 'DRAKE', 'HAG', 'IMP', 'KNIGHT', 'MAGE', 'MOTH', 'SKULL', 'SPRITE', 'WAIF', 'WITCH', 'WYRM'] as const;
const TIERS: readonly DifficultyId[] = ['apprentice', 'adept', 'archmage'];

let cached: LeaderboardEntry[] | null = null;
let inflight: Promise<LeaderboardEntry[]> | null = null;
/** In-memory handle so a mid-session rename is used even if storage is blocked. */
let sessionName: string | null = null;

function clampInt(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(value)));
}

function pick<T>(list: readonly T[]): T {
  return list[(Math.random() * list.length) | 0] ?? list[0];
}

function mintName(): string {
  return `${pick(ADJ)} ${pick(NOUN)} ${String(10 + ((Math.random() * 90) | 0))}`;
}

/** Uppercase, drop control/illegal glyphs, cap length. Does not require a complete name. */
export function filterContestantInput(raw: string): string {
  return raw.replace(CONTROL_CHARS, '').toUpperCase().replace(NAME_ILLEGAL, '').slice(0, CONTESTANT_NAME_MAX);
}

function persistName(name: string): void {
  sessionName = name;
  try {
    window.localStorage.setItem(NAME_KEY, name);
  } catch {
    // Session-only handle.
  }
}

function acceptedStoredName(value: string | null): string | null {
  return value && NAME_STORE_RE.test(value) ? value : null;
}

/** Persistent broadcast handle. Generated once per browser unless storage is blocked. */
export function contestantName(): string {
  const live = acceptedStoredName(sessionName);
  if (live) return live;
  try {
    const saved = acceptedStoredName(window.localStorage.getItem(NAME_KEY));
    if (saved) {
      sessionName = saved;
      return saved;
    }
  } catch {
    // Private mode: still hand back a name for this session.
  }
  const name = mintName();
  persistName(name);
  return name;
}

/**
 * Persist a typed handle. Empty / illegal input keeps the generated default.
 * Returns the name that is actually stored.
 */
export function setContestantName(raw: string): string {
  const name = filterContestantInput(raw).trim();
  if (NAME_INPUT_RE.test(name)) {
    persistName(name);
    return name;
  }
  return contestantName();
}

function parseEntry(data: Record<string, unknown>): LeaderboardEntry | null {
  const name = typeof data.name === 'string' && NAME_STORE_RE.test(data.name) ? data.name : null;
  const score = typeof data.score === 'number' ? clampInt(data.score, 0, MAX_SCORE) : null;
  const floor = typeof data.floor === 'number' ? clampInt(data.floor, 0, 99) : 1;
  const room = typeof data.room === 'number' ? clampInt(data.room, 0, 999) : 1;
  const kills = typeof data.kills === 'number' ? clampInt(data.kills, 0, 99999) : 0;
  const combo = typeof data.combo === 'number' ? clampInt(data.combo, 0, 9999) : 0;
  const tier = TIERS.includes(data.tier as DifficultyId) ? (data.tier as DifficultyId) : 'adept';
  const outcome: RunOutcome = data.outcome === 'victory' ? 'victory' : 'death';
  if (!name || score === null) return null;
  return { name, score, floor, room, kills, combo, tier, outcome };
}

export async function fetchTopScores(force = false): Promise<LeaderboardEntry[]> {
  if (!force && cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const db = firestore();
      if (!db) return cached ?? [];
      const snap = await getDocs(query(collection(db, SCORES_COLLECTION), orderBy('score', 'desc'), limit(LEADERBOARD_SIZE)));
      const rows: LeaderboardEntry[] = [];
      snap.forEach((doc) => {
        const row = parseEntry(doc.data());
        if (row) rows.push(row);
      });
      cached = rows;
      return rows;
    } catch (err) {
      console.warn('leaderboard fetch failed', err);
      return cached ?? [];
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function prefetchLeaderboard(): void {
  void fetchTopScores();
}

function recentlyWrote(): boolean {
  try {
    const raw = window.localStorage.getItem(WRITE_AT_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < WRITE_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markWrote(): void {
  try {
    window.localStorage.setItem(WRITE_AT_KEY, String(Date.now()));
  } catch {
    // Best-effort client cooldown.
  }
}

/** Create-only write of one finished run. Returns false if skipped or rejected. */
export async function submitRunScore(input: RunScoreInput): Promise<boolean> {
  const db = firestore();
  if (!db) return false;
  if (recentlyWrote()) return false;
  const payload = {
    name: contestantName(),
    score: clampInt(input.score, 0, MAX_SCORE),
    floor: clampInt(input.floor, 0, 99),
    room: clampInt(input.room, 0, 999),
    kills: clampInt(input.kills, 0, 99999),
    combo: clampInt(input.combo, 0, 9999),
    tier: TIERS.includes(input.tier) ? input.tier : 'adept',
    outcome: input.outcome === 'victory' ? 'victory' : 'death',
    createdAt: serverTimestamp(),
  };
  try {
    markWrote();
    await addDoc(collection(db, SCORES_COLLECTION), payload);
    cached = null;
    return true;
  } catch (err) {
    console.warn('leaderboard write failed', err);
    return false;
  }
}
